import { useCallback, useState } from 'react';

import { useTracking } from '@change-corgi/core-react/tracking';

import type { Recommendation } from 'src/app/pages/starterDashboardV2/shared/contexts';

import { SHARE_RECOMMENDATIONS_FEEDBACK_MODAL_EVENT } from '../trackingEvents';
import type { ModalResult } from '../useFeedbackModal';

export type UseFeedbackBodyModalResult = ModelHookResult<
	{
		usefulness: string[];
		extraFeedback: string;
		wasSubmitted: boolean;
	},
	{
		handleSelectChip: (value: string) => void;
		setExtraFeedback: React.Dispatch<React.SetStateAction<string>>;
		handleSubmitForm: (recommendation: Recommendation) => void;
		onClose: () => void;
	}
>;

export function useFeedbackBodyModal(closeModal: (result: ModalResult) => void) {
	const [usefulness, setUsefulness] = useState<string[]>([]);
	const [extraFeedback, setExtraFeedback] = useState('');
	const [wasSubmitted, setWasSubmitted] = useState(false);
	const track = useTracking();

	const handleSelectChip = useCallback(
		(value: string) => {
			if (!usefulness.includes(value)) {
				setUsefulness([...usefulness, value]);
				return;
			}
			const newValue = usefulness.filter((item) => item !== value);
			setUsefulness(newValue);
		},
		[usefulness, setUsefulness],
	);

	const handleSubmitForm = useCallback(
		(recommendation: Recommendation) => {
			setWasSubmitted((prevValue) => {
				if (!prevValue) {
					const feedback = usefulness;
					if (extraFeedback) {
						feedback.push(extraFeedback);
					}

					void track(SHARE_RECOMMENDATIONS_FEEDBACK_MODAL_EVENT, {
						recommendation_rating: 'dislike',
						recommendation_group_name: recommendation.name,
						recommendation_group_url: recommendation.url,
						recommendation_group_feedback: JSON.stringify(feedback),
					});
				}

				return true;
			});
		},
		[setWasSubmitted, track, extraFeedback, usefulness],
	);

	const onClose = useCallback(() => {
		closeModal({ submitted: wasSubmitted });
	}, [wasSubmitted, closeModal]);

	return {
		data: {
			usefulness,
			extraFeedback,
			wasSubmitted,
		},
		actions: {
			handleSelectChip,
			setExtraFeedback,
			handleSubmitForm,
			onClose,
		},
	};
}
